import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Library, PlusCircle, Layers } from "lucide-react";

export default function HardwareLibrary() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);

  const load = () => api.get("/library", { params: { search, category: cat } }).then((r) => setItems(r.data));
  useEffect(() => { api.get("/library/categories").then((r) => setCategories(r.data)); }, []);
  useEffect(() => { load(); }, [search, cat]);

  const openAdd = (item) => {
    setSelected(item);
    setForm({
      name: item.name + (item.variants?.length ? "" : ""),
      category: item.category, variant: item.variants?.[0] || "", unit: item.unit,
      brand: "", supplier: "", cost: Math.round(item.suggested_price * 0.8),
      price: item.suggested_price, barcode: "", sku: "", quantity: 0,
      reorder_level: 10, low_stock_level: 5, tax_status: "taxable", location: "",
    });
  };

  const addToInventory = async () => {
    try {
      await api.post(`/library/${selected.id}/add-to-inventory`, { ...form, from_library_id: selected.id });
      toast.success(`${form.name} added to your inventory`);
      setSelected(null); setForm(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><Library className="w-7 h-7 text-orange-500" /> Hardware Product Library</h1>
        <p className="text-muted-foreground">A template catalog. Browse and import items into your own inventory. This is <b>not</b> your live stock.</p>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search catalog…" className="pl-9" data-testid="library-search" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setCat("")} data-testid="cat-all"
          className={`px-3.5 py-1.5 rounded-full text-sm border ${!cat ? "bg-orange-500 text-white border-orange-500" : "border-border hover:bg-secondary"}`}>All</button>
        {categories.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c}`}
            className={`px-3.5 py-1.5 rounded-full text-sm border ${cat === c ? "bg-orange-500 text-white border-orange-500" : "border-border hover:bg-secondary"}`}>{c}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((it) => (
          <Card key={it.id} className="p-4 flex flex-col hover:border-orange-500/40 transition-colors" data-testid={`library-item-${it.id}`}>
            <div className="flex justify-between items-start">
              <p className="font-semibold leading-tight">{it.name}</p>
              <Badge className="bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20 text-[10px]">{it.subcategory}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{it.category} · per {it.unit}</p>
            {it.variants?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {it.variants.map((v) => <span key={v} className="text-[10px] mono px-1.5 py-0.5 rounded bg-secondary flex items-center gap-1"><Layers className="w-2.5 h-2.5" />{v}</span>)}
              </div>
            )}
            <div className="flex items-end justify-between mt-auto pt-3">
              <span className="mono font-bold text-emerald-600 dark:text-emerald-400">{money(it.suggested_price)}</span>
              <Button size="sm" variant="outline" onClick={() => openAdd(it)} data-testid={`add-lib-${it.id}`}>
                <PlusCircle className="w-4 h-4 mr-1.5" /> Add
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setForm(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="import-form">
          <DialogHeader><DialogTitle className="font-head">Add “{selected?.name}” to My Inventory</DialogTitle></DialogHeader>
          {form && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {selected?.variants?.length > 0 && (
                  <div className="col-span-2">
                    <Label className="text-xs">Variant / Size</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selected.variants.map((v) => (
                        <button key={v} onClick={() => setForm({ ...form, variant: v })} data-testid={`variant-${v}`}
                          className={`px-3 py-1.5 rounded-lg text-sm border ${form.variant === v ? "bg-emerald-500 text-white border-emerald-500" : "border-border"}`}>{v}</button>
                      ))}
                    </div>
                  </div>
                )}
                {[["brand", "Brand"], ["supplier", "Supplier"], ["cost", "Cost", "number"], ["price", "Selling Price", "number"],
                  ["barcode", "Barcode"], ["sku", "SKU"], ["quantity", "Quantity", "number"], ["reorder_level", "Reorder Level", "number"],
                  ["low_stock_level", "Low Stock Level", "number"], ["location", "Location"]].map(([k, lbl, type]) => (
                  <div key={k}>
                    <Label className="text-xs">{lbl}</Label>
                    <Input type={type || "text"} value={form[k]} data-testid={`imp-${k}`}
                      onChange={(e) => setForm({ ...form, [k]: type === "number" ? parseFloat(e.target.value || 0) : e.target.value })} />
                  </div>
                ))}
              </div>
              <Button onClick={addToInventory} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-import">Add to My Inventory</Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
